import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AppState } from 'common/state-type'
import Constants from 'common/utils/constants'
import throttle from 'lodash/throttle'
import useArticles from 'common/providers/useArticles'
import Project from 'common/project'
import _data from 'common/utils/_data'
import { useNavigate } from 'react-router-dom'

const SEARCH_DELAY = 500

export interface SortOption {
  label: string
  value: string
  sort: 'title' | 'published_at'
  order: 'ASC' | 'DESC'
}

export const SORT_OPTIONS: Array<SortOption> = [
  {
    label: 'Name (A-Z)',
    value: 'name-az',
    sort: 'title',
    order: 'ASC',
  },
  {
    label: 'Name (Z-A)',
    value: 'name-za',
    sort: 'title',
    order: 'DESC',
  },
  {
    label: 'Newest to Oldest',
    value: 'newest',
    sort: 'published_at',
    order: 'DESC',
  },
  {
    label: 'Oldest to Newest',
    value: 'oldest',
    sort: 'published_at',
    order: 'ASC',
  },
]
interface Author {
  id: number
  name: string
  bio: string
  author_type: string
  avatar: string | null
  linkedin_url: string | null
}

export interface BlogPost {
  id: number
  tags: string[]
  areas: string[]
  sub_areas: string[]
  author: Author
  views_count: number
  sector: string
  created_at: string // ISO date string
  updated_at: string // ISO date string
  uuid: string
  slug: string
  title: string
  summary: string
  content: string
  publish_date: string // ISO date string
  cover_image: string | null
  type: string
  is_featured: boolean
  legal_services: string[]
}

export interface AreaBlogResponse {
  count: number
  next: string | null
  previous: string | null
  results: BlogPost[]
}

export const useArticleTemplate = ({
  pageSize = 20,
}: {
  pageSize?: number
} = {}) => {
  const [sortOption, setSortOption] = useState(SORT_OPTIONS[2])
  const [articleFilter, setArticleFilter] = useState<string | undefined>(
    undefined,
  )
  const isLoadingMore = useRef(false)
  const [search, setSearch] = useState('')
  const [searchValue, setSearchValue] = useState('')

  const [page, setPage] = useState(1)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  const [selectedType, setSelectedType] = useState<string | undefined>(
    undefined,
  )
  const [selectedSector, setSelectedSector] = useState<number | undefined>(
    undefined,
  )
  const navigate = useNavigate()

  const [topAuthors, setTopAuthors] = useState<any>([])
  const [mostPublishedCategory, setMostPublishedCategory] = useState<any>([])
  const [mostViewed, setMostViewed] = useState<any>([])
  const [sectors, setSectors] = useState<any>([])
  const [tags, setTags] = useState<any>([])
  const [featuredHome, setFeaturedHome] = useState<any>([])
  const [featuredArticle, setFeaturedArticle] = useState<any>({})
  const [areaArticles, setAreaArticles] = useState<BlogPost[]>([])
  const [areaPage, setAreaPage] = useState(1)
  const [hasMoreAreaArticles, setHasMoreAreaArticles] = useState(true)

  const { articles, getArticles, articlesLoading } = useArticles()
  const [previousArticle, setPreviousArticle] = useState<
    AppState['articles']['results']
  >([])

  const throttledSetTitle = useCallback(
    throttle(setSearch, SEARCH_DELAY, { leading: true }),
    [],
  )

  const updateSearchValue = useCallback(
    (text: string) => {
      setSearchValue(text)
      throttledSetTitle(text)
    },
    [throttledSetTitle],
  )

  useEffect(() => {
    setPage(1)
    refresh()
  }, [searchValue, sortOption])

  const loadAuthors = useCallback(() => {
    _data
      .get(`${Project.liveApi}website/blogs/top-authors/`)
      .then((data) => setTopAuthors(data))
  }, [])

  const loadFeaturedHome = useCallback(() => {
    _data
      .get(
        `${Project.liveApi}website/home-page/featured-articles/
`,
      )
      .then((data) => setFeaturedHome(data))
  }, [])

  const loadFeaturedArticle = useCallback(() => {
    _data
      .get(`${Project.liveApi}website/home-page/featured-articles/`)
      .then((data) => {
        const latest = data.reduce((latestItem, currentItem) =>
          new Date(currentItem.publish_date) > new Date(latestItem.publish_date)
            ? currentItem
            : latestItem,
        )
        setFeaturedArticle(latest)
      })
  }, [])

  const loadMostViewed = useCallback(() => {
    _data
      .get(`${Project.liveApi}website/blogs/most-viewed`)
      .then((data) => setMostViewed(data))
  }, [])

  const loadSectors = useCallback(() => {
    _data
      .get(`${Project.liveApi}website/blogs/count-by-sector/`)
      .then((data) => setSectors(data))
  }, [])

  const loadMostPublishedCategory = useCallback(() => {
    _data
      .get(`${Project.liveApi}website/blogs/most-published-areas/`)
      .then((data) => setMostPublishedCategory(data))
  }, [])

  const loadTags = useCallback(() => {
    _data.get(`${Project.liveApi}website/tags`).then((data) => setTags(data))
  }, [])

  const loadArticles = useCallback(() => {
    getArticles(
      {
        is_template: Constants.isAvvokaEnabled,
        search,
        sort_by: sortOption.sort,
        order: sortOption.order,
        type: selectedType,
        document_filters: articleFilter,
        page,
        page_size: pageSize,
        sector_id: selectedSector,
      },
      {
        onSuccess: (data) => {
          setIsRefreshing(false)
          isLoadingMore.current = false
        },
        onError: () => {
          setIsRefreshing(false)
          isLoadingMore.current = false
        },
      },
    )
  }, [
    sortOption,
    search,
    page,
    selectedType,
    selectedSector,
    articleFilter,
    pageSize,
    getArticles,
  ])

  const loadMore = useCallback(() => {
    if (!articles?.next || isLoadingMore.current) {
      return
    }

    isLoadingMore.current = true

    setPreviousArticle([...previousArticle, ...(articles?.results ?? [])])
    setPage(page + 1)
  }, [articles, page])

  const refresh = useCallback(() => {
    setIsRefreshing(true)
    setPreviousArticle([])
    loadArticles()
  }, [loadArticles])

  const results = useMemo(() => {
    const docs = [...previousArticle, ...(articles?.results ?? [])]

    // Check for uniqueness based on ID, just in case.
    return docs.filter(
      (value, index, self) =>
        index === self.findIndex(({ id }) => id === value.id),
    )
  }, [previousArticle, articles])

  const loadArticlesByArea = useCallback(
    (type: 'area' | 'sub_area', value: string, pageSize = 4) => {
      setAreaPage(1)
      _data
        .get(
          `${Project.liveApi}website/blogs/?${type}=${value}&page_size=${pageSize}`,
        )
        .then((data: AreaBlogResponse) => {
          setAreaArticles(data.results)
          setHasMoreAreaArticles(!!data.next)
        })
    },
    [],
  )

  const loadMoreArticlesByArea = useCallback(
    (type: 'area' | 'sub_area', value: string, pageSize = 4) => {
      if (!hasMoreAreaArticles) return

      const nextPage = areaPage + 1
      _data
        .get(
          `${Project.liveApi}website/blogs/?${type}=${value}&page_size=${pageSize}&page=${nextPage}`,
        )
        .then((data: AreaBlogResponse) => {
          setAreaArticles((prev) => [...prev, ...data.results])
          setHasMoreAreaArticles(!!data.next)
          setAreaPage(nextPage)
        })
    },
    [areaPage, hasMoreAreaArticles],
  )

  return {
    results,
    topAuthors,
    isLoading: articlesLoading,
    loadMore,
    selectedType,
    setSelectedType,
    sortOption,
    setSortOption,
    articleFilter,
    setArticleFilter,
    searchValue,
    updateSearchValue,
    hasNext: !!articles?.next,
    refresh,
    isRefreshing,
    mostPublishedCategory,
    mostViewed,
    sectors,
    tags,
    setTags,
    loadArticles,
    loadAuthors,
    loadMostPublishedCategory,
    loadMostViewed,
    loadSectors,
    loadTags,
    loadFeaturedHome,
    loadArticlesByArea,
    loadMoreArticlesByArea,
    featuredHome,
    areaArticles,
    hasMoreAreaArticles,
    loadFeaturedArticle,
    featuredArticle,
    setSelectedSector,
    selectedSector,
  }
}
